// Accessible help affordance: a slate Info icon button that reveals a short
// definition. The definition is always present in the DOM as a visually-hidden
// node (owns aria-describedby) so screen readers get it regardless of open
// state; the visual overlay is portalled to document.body so it is never
// clipped by a scrolling modal body. Two open modes: transient (hover/focus,
// closes on mouseleave/blur) and pinned (click/tap, cleared by Escape or
// outside-click). Renders null when there is no definition.

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";

const GAP_PX = 8;

export interface InfoHintProps {
  text: string;
  label?: string;
}

export default function InfoHint({ text, label = "Definition" }: InfoHintProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const srId = useId();
  const [transient, setTransient] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const open = transient || pinned;

  const place = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + GAP_PX, left: rect.left });
  }, []);

  const showTransient = useCallback(() => {
    place();
    setTransient(true);
  }, [place]);

  const togglePin = useCallback(() => {
    place();
    setPinned((p) => !p);
  }, [place]);

  // Outside-click + Escape clear the pin (only while pinned).
  useEffect(() => {
    if (!pinned) return;
    const onDown = (e: MouseEvent) => {
      if (!triggerRef.current?.contains(e.target as Node)) setPinned(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPinned(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [pinned]);

  if (text.trim() === "") return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={srId}
        onMouseEnter={showTransient}
        onMouseLeave={() => setTransient(false)}
        onFocus={showTransient}
        onBlur={() => setTransient(false)}
        onClick={togglePin}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setPinned(false);
            setTransient(false);
          }
        }}
        className="inline-flex items-center justify-center rounded-sm text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 focus:outline-hidden focus:ring-2 focus:ring-brand-400"
      >
        <Info className="h-3.5 w-3.5" aria-hidden="true" />
      </button>

      {/* Screen-reader copy — always present, owns the described-by id. */}
      <span id={srId} className="sr-only">{text}</span>

      {open &&
        pos &&
        createPortal(
          <div
            // Visual-only; the SR copy above carries the described-by id, so this
            // node deliberately has no id (no duplicate-content for assistive tech).
            role="presentation"
            style={{ top: pos.top, left: pos.left }}
            className="fixed z-50 max-w-[18rem] rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-caption text-slate-700 dark:text-slate-300 shadow-soft-lg dark:shadow-soft-lg-dark animate-fade-in [animation-duration:120ms] motion-reduce:animate-none"
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  );
}
